import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import protectionIcon from "../assets/Images/protection-icon.svg";
import { useTheme } from "@mui/material/styles";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import CryptoJS from "crypto-js";

const PASSKEY_SECRET = process.env.REACT_APP_PASSKEY_SECRET || "NexusPasskeySecret2024";

export default function ProtectedAccessModal({ open, onSuccess }) {
  const theme = useTheme();
  const [passkey, setPasskey] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // const handleSubmit = () => {
  //   if (passkey === STATIC_PASSKEY) {
  //     setError("");
  //     onSuccess();
  //   } else {
  //     setError("Invalid passkey");
  //   }
  // };

  const handleSubmit = async () => {
    if (!passkey.trim()) {
      setError("Please enter a passkey");
      return;
    }

    try {
      setLoading(true);
      // Encrypt passkey before sending
      const encryptedPasskey = CryptoJS.AES.encrypt(passkey, PASSKEY_SECRET).toString();

      await API.post(api_paths.PASSKEY.verify, { pass_key: encryptedPasskey });

      // Persist authorisation in a 1-day cookie
      const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `dev_access=1; expires=${expires}; path=/; SameSite=Strict`;

      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || "Invalid passkey");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: "1px solid",
          borderColor: "grey.600",
          boxShadow: "none", // Optional: usually looks better with a clean border
        },
      }}
    >
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          py={2}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "grey.200",
            }}
          >
            <img
              src={protectionIcon}
              alt="Protection Icon"
              height={30}
              width={30}
            />
          </Box>

          {/* Title */}
          <Typography variant="h6" fontWeight={900}>
            Protected Access
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Enter the passkey to view our developers
          </Typography>

          {/* Input */}
          <TextField
            fullWidth
            autoComplete="off"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "grey.50",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "grey.100",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.success.main,
                },
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 4,
                height: 50,
                my: 1,
              },
            }}
            type={showPassword ? "text" : "password"}
            placeholder="Enter passkey..."
            value={passkey}
            onChange={(e) => setPasskey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit();
              }
            }}
            error={!!error}
            helperText={error}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Button */}
          {/* <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#8c95a3",
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              borderRadius: 2,
            }}
            onClick={handleSubmit}
          >
            Unlock Access
          </Button> */}
          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#8c95a3",
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              borderRadius: 2,
            }}
            onClick={handleSubmit}
            startIcon={
              loading && <CircularProgress size={16} color="inherit" />
            }
          >
            {loading ? "Verifying..." : "Unlock Access"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
